import { isDef } from "@redotech/util/checks";
import { assertNever } from "@redotech/util/type";

const basicCheckoutAnalyticsVersionEnum = [
  "basic", // If they don't have Shopify Plus, we can't use advanced analytics
] as const;

// Advanced versions backed by the Shop+ CHECKOUT_INFO_CHANGED event +
// ADVANCED_CHECKOUT_CONVERSION_METRICS mart.
const shopPlusAdvancedCheckoutAnalyticsVersionEnum = [
  "advanced-v08-13-2025", // ADVANCED_CHECKOUT_CONVERSION_METRICS_EARLIEST_DATE_TO_USE_FOR_ALL_SPLIT_METRICS
  "advanced-v09-16-2025", // When delivery options started being recorded on CHECKOUT_INFO_CHANGED customer events
] as const;

// Advanced versions for NON-Shop-Plus merchants, backed by CHECKOUT_RATES_SHOWN
// visits + enriched ORDER_CREATED orders joined in the
// NON_PLUS_CHECKOUT_CONVERSION_METRICS mart. These resolve to the same Snowflake
// segment-metrics read path as the Shop+ versions (so they count as "advanced"),
// but point at a different table with a thinner dimension set.
const nonPlusCheckoutAnalyticsVersionEnum = [
  "non-plus-v06-02-2026", // When granular non-Plus analytics (rates-shown + order join) became available
] as const;

const advancedCheckoutAnalyticsVersionEnum = [
  ...shopPlusAdvancedCheckoutAnalyticsVersionEnum,
  ...nonPlusCheckoutAnalyticsVersionEnum,
] as const;
export type AdvancedCheckoutAnalyticsVersion =
  (typeof advancedCheckoutAnalyticsVersionEnum)[number];

export type NonPlusCheckoutAnalyticsVersion =
  (typeof nonPlusCheckoutAnalyticsVersionEnum)[number];

export const checkoutAnalyticsVersionEnum = [
  ...basicCheckoutAnalyticsVersionEnum,
  ...advancedCheckoutAnalyticsVersionEnum,
] as const;
export type CheckoutAnalyticsVersion =
  (typeof checkoutAnalyticsVersionEnum)[number];

export const latestAdvancedCheckoutAnalyticsVersion: AdvancedCheckoutAnalyticsVersion =
  "advanced-v09-16-2025";
export const ADVANCED_CHECKOUT_CONVERSION_METRICS_EARLIEST_DATE_TO_USE_DELIVERY_OPTIONS =
  new Date("2025-09-16T00:00:00Z");
export const ADVANCED_CHECKOUT_CONVERSION_METRICS_EARLIEST_AVAILABLE_DATE =
  new Date("2025-07-27T12:00:00Z");
export const ADVANCED_CHECKOUT_CONVERSION_METRICS_EARLIEST_DATE_TO_USE_FOR_ALL_SPLIT_METRICS =
  new Date("2025-08-13T00:00:00Z");

// Splits started after this date are eligible for the granular non-Plus
// analytics version. Hard-coded for now; adjust as the mart accumulates history.
export const NON_PLUS_GRANULAR_ANALYTICS_CUTOVER_DATE = new Date(
  "2026-06-02T00:00:00Z",
);

// Typed as ReadonlySet<string> so membership checks accept the wider
// CheckoutAnalyticsVersion union without a cast (a const-tuple's own
// .includes()/.has() would only accept its narrow element type).
const advancedCheckoutAnalyticsVersions: ReadonlySet<string> = new Set(
  advancedCheckoutAnalyticsVersionEnum,
);
const nonPlusCheckoutAnalyticsVersions: ReadonlySet<string> = new Set(
  nonPlusCheckoutAnalyticsVersionEnum,
);

export function isAdvancedCheckoutAnalyticsVersion(
  version: CheckoutAnalyticsVersion,
): version is AdvancedCheckoutAnalyticsVersion {
  return advancedCheckoutAnalyticsVersions.has(version);
}

export function isNonPlusCheckoutAnalyticsVersion(
  version: CheckoutAnalyticsVersion,
): version is NonPlusCheckoutAnalyticsVersion {
  return nonPlusCheckoutAnalyticsVersions.has(version);
}

export function getCheckoutAnalyticsVersionForSplit({
  shouldUseAdvanced,
  shouldUseNonPlusGranular = false,
  splitStartDate,
  splitAnalyticsVersionOverride,
}: {
  shouldUseAdvanced: boolean;
  // Whether this (non-Shop-Plus) team is eligible for the granular non-Plus
  // analytics version. Defaults to false so this resolver is dark until a caller
  // (Phase 6/7 rollout) opts a team in — every existing caller keeps its current
  // behavior.
  shouldUseNonPlusGranular?: boolean;
  splitStartDate: Date | undefined;
  splitAnalyticsVersionOverride: CheckoutAnalyticsVersion | undefined;
}): CheckoutAnalyticsVersion {
  if (isDef(splitAnalyticsVersionOverride)) {
    switch (splitAnalyticsVersionOverride) {
      case "basic":
        // No reason to prevent override to basic
        return "basic";
      case "advanced-v08-13-2025":
        if (
          splitStartDate &&
          splitStartDate.getTime() >
            ADVANCED_CHECKOUT_CONVERSION_METRICS_EARLIEST_DATE_TO_USE_FOR_ALL_SPLIT_METRICS.getTime()
        ) {
          return "advanced-v08-13-2025";
        }
        break;
      case "advanced-v09-16-2025":
        if (
          splitStartDate &&
          splitStartDate.getTime() >
            ADVANCED_CHECKOUT_CONVERSION_METRICS_EARLIEST_DATE_TO_USE_DELIVERY_OPTIONS.getTime()
        ) {
          return "advanced-v09-16-2025";
        }
        break;
      case "non-plus-v06-02-2026":
        if (
          splitStartDate &&
          splitStartDate.getTime() >
            NON_PLUS_GRANULAR_ANALYTICS_CUTOVER_DATE.getTime()
        ) {
          return "non-plus-v06-02-2026";
        }
        break;
      default:
        assertNever(splitAnalyticsVersionOverride);
    }
  }

  if (
    shouldUseAdvanced &&
    splitStartDate &&
    splitStartDate.getTime() >
      ADVANCED_CHECKOUT_CONVERSION_METRICS_EARLIEST_DATE_TO_USE_DELIVERY_OPTIONS.getTime()
  ) {
    return "advanced-v09-16-2025";
  } else if (
    shouldUseAdvanced &&
    splitStartDate &&
    splitStartDate.getTime() >
      ADVANCED_CHECKOUT_CONVERSION_METRICS_EARLIEST_DATE_TO_USE_FOR_ALL_SPLIT_METRICS.getTime()
  ) {
    return "advanced-v08-13-2025";
  } else if (
    // Non-Plus teams (not Shop+ advanced-eligible) opted into granular analytics,
    // for splits started after the cutover. Shop+ branches take precedence above;
    // in practice the two eligibilities are mutually exclusive.
    shouldUseNonPlusGranular &&
    splitStartDate &&
    splitStartDate.getTime() >
      NON_PLUS_GRANULAR_ANALYTICS_CUTOVER_DATE.getTime()
  ) {
    return "non-plus-v06-02-2026";
  } else {
    return "basic";
  }
}
